import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMap } from "react-leaflet";
import L from "leaflet";
import classNames from "classnames";
import {
  CameraPosition,
  selectEnvironmentInteractionPoints,
} from "../../app/slices/EnvironmentSlice";
import {
  selectCameraPickingFitRequestId,
  selectExpandedTakeLocationIds,
  setExpandedTakeLocationIds,
} from "../../app/slices/GlobalSlice";
import {
  selectSelectedElement,
  selectSelectedElementId,
  selectSelectedEnvironmentLayerIds,
  selectSelectedTake3D,
} from "../../app/slices/TrainingSlice";
import { ISetCamera } from "../../models/interactions/SetCamera";
import CameraRenderPreview from "./CameraRenderPreview";
import {
  filterTakePositionsByLayers,
  resolveCameraTakeLocation,
} from "./environmentLayerVisibility";

/** Below this map width the shot card shrinks so the cameras keep room beside it. */
const NARROW_MAP_WIDTH = 900;
const CARD_MARGIN = 16;
/** Space kept clear around the fitted cameras, and next to the card and the Set Camera bar. */
const FIT_PADDING = 40;
const SET_CAMERA_BAR_HEIGHT = 96;
/** Selecting the interaction zooms this many levels closer than "fit every camera". */
const FOCUS_ZOOM_IN = 2;

const prefersReducedMotion = () =>
  !!window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

/**
 * Camera picking (take camera or a Set Camera interaction), design Design Studio
 * set-camera-ux/4d: the shot is docked in the map's top-right corner and the map
 * fits the take location's cameras into the space beside it, so the card never
 * covers one. The card shows the selected camera; hovering a camera shows its
 * shot in a popover on the map (CameraHoverPreview).
 */
export default function Viewport3DCameraPicking() {
  const map = useMap();
  const selectedElement = useSelector(selectSelectedElement) as ISetCamera | undefined;
  const selectedElementId = useSelector(selectSelectedElementId);
  const take = useSelector(selectSelectedTake3D);
  const takePositions = useSelector(selectEnvironmentInteractionPoints);
  const fitRequestId = useSelector(selectCameraPickingFitRequestId);
  const visibleLayerIds = useSelector(selectSelectedEnvironmentLayerIds);
  const expandedTakeLocationIds = useSelector(selectExpandedTakeLocationIds);
  const dispatch = useDispatch();

  const [mapSize, setMapSize] = useState(() => map.getSize());
  const [isPointerNearCard, setIsPointerNearCard] = useState(false);

  const cameraId = selectedElement?.cameraId ?? "";
  // The cameras to fit and pan to: those of the selected camera's take location (a Set
  // Camera may cut to another location), else the take's own. Hidden layers are skipped.
  const visibleTakePositions = useMemo(
    () =>
      filterTakePositionsByLayers(
        takePositions,
        visibleLayerIds ?? [],
        takePositions.find((p) => p.id === take?.interactionPointId)?.layer
      ),
    [takePositions, visibleLayerIds, take?.interactionPointId]
  );
  const location = resolveCameraTakeLocation(
    visibleTakePositions,
    selectedElement?.takeLocationId,
    take
  );
  const locationId = location?.id ?? "";
  const cameras = useMemo(
    () =>
      location && take?.situations[location.id] === undefined
        ? location.camera_positions
        : [],
    [location, take?.situations]
  );
  const selectedCamera = cameras.find((c) => c.id === cameraId);

  const cardWidth = mapSize.x < NARROW_MAP_WIDTH ? 240 : 340;
  const cardHeight = ((cardWidth - 16) * 9) / 16 + 16;
  // Both a Set Camera interaction and the take camera show the camera picking bar below.
  const bottomPadding = SET_CAMERA_BAR_HEIGHT + FIT_PADDING;
  const paddingTopLeft = L.point(FIT_PADDING, FIT_PADDING);
  const paddingBottomRight = L.point(cardWidth + CARD_MARGIN + FIT_PADDING, bottomPadding);

  useEffect(() => {
    const onResize = () => setMapSize(map.getSize());
    map.on("resize", onResize);
    return () => {
      map.off("resize", onResize);
    };
  }, [map]);

  // Fade the card while the pointer is near it, so a camera that ends up behind it
  // (after a manual pan or zoom) can still be seen and clicked.
  useEffect(() => {
    const onMove = (e: L.LeafletMouseEvent) => {
      const size = map.getSize();
      const near = 24;
      setIsPointerNearCard(
        e.containerPoint.x >= size.x - CARD_MARGIN - cardWidth - near &&
          e.containerPoint.y <= CARD_MARGIN + cardHeight + near
      );
    };
    const onOut = () => setIsPointerNearCard(false);
    map.on("mousemove", onMove);
    map.on("mouseout", onOut);
    return () => {
      map.off("mousemove", onMove);
      map.off("mouseout", onOut);
    };
  }, [map, cardWidth, cardHeight]);

  // Another take location is folded into a pin; unfold it so its cameras show.
  useEffect(() => {
    if (!locationId || locationId === take?.interactionPointId) return;
    if ((expandedTakeLocationIds ?? []).includes(locationId)) return;
    dispatch(setExpandedTakeLocationIds([...(expandedTakeLocationIds ?? []), locationId]));
  }, [dispatch, locationId, take?.interactionPointId, expandedTakeLocationIds]);

  // Latest geometry for the effect below, which should only run on its own triggers.
  const latest = useRef({ cameras, selectedCamera, paddingTopLeft, paddingBottomRight });
  latest.current = { cameras, selectedCamera, paddingTopLeft, paddingBottomRight };

  // Selecting the interaction (or a camera at another location) focuses the selected camera, a little closer than the
  // zoom that fits every camera of the location; "fit cameras" frames them all.
  // Changing the camera afterwards only pans, and only if the new camera is out of
  // the free area: picking one on the map never moves the map under the pointer.
  const lastTriggers = useRef<{
    selection?: string;
    location?: string;
    camera?: string;
    fit?: number;
  }>({});
  useEffect(() => {
    const previous = lastTriggers.current;
    lastTriggers.current = {
      selection: selectedElementId,
      location: locationId,
      camera: cameraId,
      fit: fitRequestId,
    };
    const { cameras, selectedCamera, paddingTopLeft, paddingBottomRight } = latest.current;
    const toLatLng = (c: CameraPosition) =>
      map.unproject([512 * c.position.x, 512 * c.position.y], 1);
    const animate = !prefersReducedMotion();

    // A camera at another take location moves the map there like a fresh selection.
    if (previous.selection !== selectedElementId || previous.location !== locationId) {
      if (cameras.length === 0) return;
      const bounds = L.latLngBounds(cameras.map(toLatLng));
      const fitZoom = map.getBoundsZoom(bounds, false, paddingTopLeft.add(paddingBottomRight));
      const zoom = Math.min(fitZoom + FOCUS_ZOOM_IN, map.getMaxZoom());
      const focus = selectedCamera ? toLatLng(selectedCamera) : bounds.getCenter();
      // Put the focus in the middle of the free area (left of the card, above the bar),
      // not in the middle of the map.
      const size = map.getSize();
      const freeCenter = paddingTopLeft.add(size.subtract(paddingBottomRight)).divideBy(2);
      const offset = size.divideBy(2).subtract(freeCenter);
      map.setView(map.unproject(map.project(focus, zoom).add(offset), zoom), zoom, { animate });
    } else if (previous.fit !== fitRequestId) {
      if (cameras.length === 0) return;
      map.fitBounds(L.latLngBounds(cameras.map(toLatLng)), {
        paddingTopLeft,
        paddingBottomRight,
        maxZoom: map.getMaxZoom(),
        animate,
      });
    } else if (previous.camera !== cameraId && selectedCamera) {
      map.panInside(toLatLng(selectedCamera), { paddingTopLeft, paddingBottomRight, animate });
    }
  }, [map, selectedElementId, locationId, cameraId, fitRequestId]);

  return (
    <>
      <div
        className={classNames(
          "absolute z-[800] p-[6px] box-border rounded-[8px] border-2 bg-creator-surface",
          "shadow-[0_16px_40px_rgba(16,24,40,0.12)] pointer-events-none transition-opacity duration-150",
          "border-solid border-interaction-primary",
          { "opacity-20": isPointerNearCard }
        )}
        style={{ top: CARD_MARGIN, right: CARD_MARGIN, width: cardWidth }}
        data-testid="camera-shot-card"
      >
        <CameraRenderPreview cameraId={cameraId} />
      </div>
    </>
  );
}
